import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId } from '@leanin/shared/ids';
import { useMember } from '@/lib/use-session';
import { getApiClient } from '@/lib/api-client';
import { MessagesClient } from './-components/MessagesClient';

type MessagesSearch = {
  c?: string;
  compose?: string;
  q?: string;
  unread?: 'true' | 'false';
};

/**
 * Single source of truth for `/messages` URLs. All callers route through
 * here so the search-param shape (`c`, `compose`) and the deep-link hash
 * convention (`#message-<id>`) stay in lockstep with `validateSearch`
 * above — a future rename ripples through one file, not the codebase.
 *
 * Pass either an existing `conversationId` (lands on that thread) or a
 * `composeUserId` (route find-or-creates a DM with that user). Optional
 * `messageId` adds the deep-link hash so the client scrolls to a specific
 * message.
 */
export function messagesHref(opts: {
  readonly conversationId?: string;
  readonly composeUserId?: string;
  readonly messageId?: string;
}): string {
  const params = new URLSearchParams();
  if (opts.conversationId) params.set('c', opts.conversationId);
  if (opts.composeUserId) params.set('compose', opts.composeUserId);
  const qs = params.toString();
  const hash = opts.messageId ? `#message-${opts.messageId}` : '';
  return `/messages${qs ? `?${qs}` : ''}${hash}`;
}

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_authed/messages/')({
  validateSearch: (raw: Record<string, unknown>): MessagesSearch => ({
    ...(pickStr(raw.c) ? { c: pickStr(raw.c) as string } : {}),
    ...(pickStr(raw.compose) ? { compose: pickStr(raw.compose) as string } : {}),
    ...(pickStr(raw.q) ? { q: pickStr(raw.q) as string } : {}),
    ...(raw.unread === 'true' ? { unread: 'true' as const } : {}),
  }),
  component: MessagesPage,
});

async function findOrCreateDirectConversation(userId: string): Promise<string | null> {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/api/v1/conversations', {
    body: { kind: 'dm', userIds: [asUserId(userId)] },
  });
  if (data !== undefined) return data.data.id;
  if (response.status === 409) {
    const body = error as
      | { error?: { details?: { conversationId?: string } } }
      | undefined;
    const existing = body?.error?.details?.conversationId;
    if (existing !== undefined) return existing;
  }
  return null;
}

function MessagesPage() {
  const search = Route.useSearch();
  const member = useMember();

  const composeMutation = useMutation({
    mutationFn: (userId: string) => findOrCreateDirectConversation(userId),
  });

  const compose = search.compose;
  const shouldCompose =
    compose !== undefined &&
    isUuid(compose) &&
    member !== null &&
    compose !== member.id;

  // Kick off compose-conversation creation once, when the search param asks
  // for it and the viewer is known. The mutation result feeds `initialId`
  // below — MessagesClient picks it up via the `initialId` change effect.
  useEffect(() => {
    if (
      shouldCompose &&
      compose !== undefined &&
      !composeMutation.isPending &&
      composeMutation.data === undefined &&
      composeMutation.error === null
    ) {
      composeMutation.mutate(compose);
    }
  }, [shouldCompose, compose, composeMutation]);

  const composeResolvedId = composeMutation.data ?? null;
  const initialId = composeResolvedId ?? search.c ?? null;

  return <MessagesClient initialId={initialId} />;
}
